import { removeCountryCode } from "../../../globals/utils/commons";
import { buildFullName } from "../../../globals/utils/nameUtils";
import { is_prod } from "../../../globals/utils/constants";

export function calculateBlanketPremium(bondTerm) {
  const t = (bondTerm || "").trim();
  switch (t) {
    case "1 year Bond":
    case "1 Year Bond":
      return 175.0;
    case "2 year Bond":
    case "2 Year Bond":
      return 306.0;
    case "3 year Bond":
    case "3 Year Bond":
      return 437.0;
    default:
      return 175.0;
  }
}

export function prefillBlanketBondForm(setValue, bond, blanketbond, response) {
  setValue("firstName", blanketbond.first_name || "");
  setValue("middleName", blanketbond.middle_name || "");
  setValue("lastName", blanketbond.last_name || "");
  setValue("preferredName", blanketbond.preferred_name || "");
  setValue("dob", blanketbond.dob);
  setValue("doe", blanketbond.date_of_expiration);
  setValue("address", blanketbond.street_address_line_1 || "");
  setValue("city", blanketbond.city || "");
  setValue("state", blanketbond.state || "");
  setValue("zipCode", blanketbond.zip || "");
  setValue("cell_phone", blanketbond.cell_phone || "");
  setValue("mayWeText", blanketbond.should_we_text ? "Yes" : "No");
  setValue("email", blanketbond.email || "");
  setValue("is_esignatures", bond.is_esignatures ? "Yes" : "No");
  setValue("usCitizen", blanketbond.is_us_citizen ? "Yes" : "No");
  setValue("residencyStatus", blanketbond.residency_status || "");
  setValue("bankrupt", blanketbond.bankrupcy ? "Yes" : "No");
  setValue("bankruptExplanation", blanketbond.bankrupcy_explanation || "");
  setValue("lawsuits", blanketbond.judgements ? "Yes" : "No");
  setValue("lawsuitsExplanation", blanketbond.judgements_explanation || "");
  setValue("declinedSurety", blanketbond.is_decline ? "Yes" : "No");
  setValue("suretyExplanation", blanketbond.declined_explanation || "");
  setValue("replacingBond", blanketbond.is_replace ? "Yes" : "No");
  setValue("bondReplacementReason", blanketbond.surety_name_reason || "");
  setValue("authorizationAgreement", blanketbond.authorization_underwriting);
  setValue("ssn", blanketbond.ssn);
  setValue("cancellationDocument", blanketbond.cancellation_document || "");
  setValue("cancellationDocumentLink", blanketbond.cancellation_document_link || "");
  setValue("creditDocument", blanketbond.credit_report_document || "");
  setValue("creditDocumentLink", blanketbond.credit_report_document_link || "");
  setValue("fcraDocument", blanketbond.fcra_document || "");
  setValue("fcraDocumentLink", blanketbond.fcra_document_link || "");
  setValue("verificationCertDocument", blanketbond.verification_certificate_document || "");
  setValue("verificationCertDocumentLink", blanketbond.verification_certificate_document_link || "");
  setValue("verificationPoaDocument", blanketbond.verification_poa_document || "");
  setValue("verificationPoaDocumentLink", blanketbond.verification_poa_document_link || "");
  setValue("bondTerm", bond.bond_sub_category || "");
  setValue("is_underwriting", bond.is_underwriting || false);
  setValue("underwriting_status", response.underwriting_status || "Not Required");
  setValue("underwriting_reason", response.underwriting_reason || "N/A");
  setValue("overrided_by", response.overrided_by || "");
  setValue("county", bond.county || "");
  setValue("number", bond.bond_number);
  setValue("obligeeId", bond.obligee || null);
  setValue("bondAppFileName", bond.first_document_name || "No file uploaded");
  setValue("bondAppLink", bond.first_document_url || "");
  setValue("bondFileName", bond.second_document_name || "No file uploaded");
  setValue("bondLink", bond.second_document_url || "");
  setValue("indemnityFileName", bond.third_document_name || "No file uploaded");
  setValue("indemnityLink", bond.third_document_url || "");
}

export function formatBondDate(date) {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

export function resolveIssueDatePayload(option, formData, currentDate) {
  if (option.effective_date != null && option.effective_date !== "") {
    return option.effective_date;
  }
  if (formData.doe) {
    return formatBondDate(formData.doe);
  }
  return currentDate;
}

export function computeBlanketExpirationDate(data) {
  const bondTermMatch = data.bondTerm?.match(/^(\d+)\s*[Yy]ear\s*[Bb]ond$/);
  const bondYears = bondTermMatch ? Number.parseInt(bondTermMatch[1], 10) : 0;

  let baseDate = data.doe ? new Date(data.doe) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    baseDate = new Date();
  }

  baseDate.setFullYear(baseDate.getFullYear() + bondYears);

  return `${(baseDate.getMonth() + 1).toString().padStart(2, "0")}/${baseDate
    .getDate()
    .toString()
    .padStart(2, "0")}/${baseDate.getFullYear()}`;
}

export function buildBlanketApprovalPayload(data, option, ctx) {
  return {
    is_prod: is_prod,
    bond_id: ctx.bondId,
    ams_flow: option.ams_flow,
    general_indemnity: false,
    send_to_vermost: true,
    patch_type: "approve",
    approver: "dvermost@vermost.com",
    bond_type: "Blanket",
    bond_sub_category: data.bondTerm,
    bond_description: "Blanket Bond - " + data.bondTerm,
    bond_amount: "$50,000",
    bond_number: ctx.bondNumber,
    input_by: ctx.userFullName,
    bond_requestor_fullname: ctx.userFullName,
    bond_requestor_email: data.email,
    customer_no: ctx.customerNo,
    premium: calculateBlanketPremium(data.bondTerm),
    county: data.county,
    name: buildFullName(data.firstName, data.middleName, data.lastName),
    address: `${data.address}, ${data.city}, ${data.state}, ${data.zipCode}`,
    phone: removeCountryCode(data.cell_phone),
    signed_date: ctx.currentDate,
    issue_date: ctx.issueDatePayload,
    expiration_date: ctx.expirationDatePayload,
    is_us_citizen: data.usCitizen,
    residency: data.usCitizen === "Yes" ? "USA" : "",
    is_bankrupt: data.bankrupt,
    is_bankrupt_explanation: data.bankruptExplanation,
    is_lawsuit: data.lawsuits,
    is_lawsuit_explanation: data.lawsuitsExplanation,
    is_declined: data.declinedSurety,
    is_declined_explanation: data.suretyExplanation,
    prior_surety: data.declinedSurety,
    prior_surety_explanation: data.suretyExplanation,
    is_successor: data.replacingBond,
    is_successor_explanation: data.bondReplacementReason,
    type_bond_permit: "Florida Professional Guardian Blanket Bond",
    obligee: "Governor of the State of Florida",
    estate_of: buildFullName(data.firstName, data.middleName, data.lastName),
    estate_of_first_name: data.firstName,
    estate_of_middle_name: data.middleName,
    estate_of_last_name: data.lastName,
    estate_of_suffix: "",
    is_verification_certificate: option.approval === "verification",
    principal1: {
      name: buildFullName(data.firstName, data.middleName, data.lastName),
      street: data.address,
      city: data.city,
      state: data.state,
      zip: data.zipCode,
      us_citizen: data.usCitizen,
      citizenship: data.usCitizen === "Yes" ? "USA" : "",
    },
    create_new_customer: ctx.pgData !== null && ctx.pgData.ams_customer_number === null,
    customer_info: {
      type: "Customer",
      names: "Business",
      firm_name: buildFullName(data.firstName, data.middleName, data.lastName),
      address: data.address,
      city: data.city,
      state: data.state,
      zip: data.zipCode,
      executive: "Darren Vermost",
      representative: "Darren Vermost",
      department: "Bonds",
      phone_number: removeCountryCode(data.cell_phone) || "",
      email: data.email || "",
    },
    billing_method: option.billing_method,
  };
}

export function buildBlanketApprovalStatusUpdatePayload(option) {
  return {
    bond: {
      status: "Approved",
      updated_at: new Date().toISOString(),
      approved_at: new Date().toISOString(),
      billing_method: option.billing_method,
      liberty_api_status: option.approval === "verification" ? "approved" : "pending",
    },
    blanketbond: {},
  };
}

export function buildBlanketUpdatePayload(data, {
  billingMethod,
  isIndemnityReceived,
  editableCreatedAt,
}) {
  return {
    bond: {
      bond_carrier: is_prod ? 7 : 4,
      bond_type: 5,
      bond_sub_category: data.bondTerm,
      bond_amount: 50000.0,
      premium: calculateBlanketPremium(data.bondTerm),
      state: 6,
      county: data.county,
      is_esignatures: data.is_esignatures === "Yes",
      name: buildFullName(data.firstName, data.middleName, data.lastName),
      obligee: data.obligeeId,
      updated_at: new Date().toISOString(),
      billing_method: billingMethod || "Direct Bill",
      ...(editableCreatedAt ? { created_at: new Date(editableCreatedAt).toISOString() } : {}),
    },
    blanketbond: {
      first_name: data.firstName,
      middle_name: data.middleName,
      last_name: data.lastName,
      preferred_name: data.preferredName,
      street_address_line_1: data.address,
      street_address_line_2: "",
      city: data.city,
      state: data.state,
      zip: data.zipCode,
      cell_phone: data.cell_phone,
      should_we_text: data.mayWeText === "Yes",
      email: data.email,
      dob: data.dob,
      date_of_expiration: data.doe,
      ssn: data.ssn,
      is_us_citizen: data.usCitizen === "Yes",
      residency_status: data.residencyStatus,
      bankrupcy: data.bankrupt === "Yes",
      bankrupcy_explanation: data.bankruptExplanation,
      judgements: data.lawsuits === "Yes",
      judgements_explanation: data.lawsuitsExplanation,
      is_decline: data.declinedSurety === "Yes",
      declined_explanation: data.suretyExplanation,
      is_replace: data.replacingBond === "Yes",
      surety_name_reason: data.bondReplacementReason,
      authorization_underwriting: data.authorizationAgreement,
      bond_term: data.bondTerm,
      updated_at: new Date().toISOString(),
      is_indemnity_received:
        isIndemnityReceived === "" ? null : isIndemnityReceived === "Yes",
      fcra_document: data.fcraDocument || "",
      fcra_document_link: data.fcraDocumentLink || "",
    },
  };
}
